<template>
    <ais-refinement-list
      :attribute="attribute"
      searchable
      :show-more="false"
      :limit="1000"
    >
      <template
        v-slot="{
          items,
          isShowingMore,
          isFromSearch,
          canToggleShowMore,
          refine,
          createURL,
          toggleShowMore,
          searchForItems,
          sendEvent,
        }"
      >
        <label :for="itemKey('input')"
               class="block mb-2 text-sm font-medium text-gray-900 dark:text-white">{{label}}</label>
        <div class="relative mb-4" v-show="searchable">
          <div class="absolute inset-y-0 right-0 flex items-center pr-3.5 pointer-events-none">
            <svg class="w-3 h-3 text-gray-500 dark:text-gray-400" xmlns="http://www.w3.org/2000/svg"
                 width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
              <g clip-path="url(#clip0_824_1500)">
                <path
                    d="M4.80006 9.6C3.8507 9.6 2.92266 9.31848 2.13329 8.79105C1.34392 8.26362 0.72869 7.51396 0.365385 6.63688C0.00207977 5.75979 -0.0929773 4.79468 0.0922339 3.86357C0.277445 2.93246 0.734606 2.07718 1.40591 1.40589C2.07721 0.734597 2.93249 0.277442 3.86361 0.0922328C4.79473 -0.0929762 5.75986 0.00207975 6.63696 0.36538C7.51406 0.728681 8.26372 1.34391 8.79116 2.13326C9.3186 2.92262 9.60011 3.85065 9.60011 4.8C9.59868 6.0726 9.09251 7.29267 8.19263 8.19253C7.29276 9.0924 6.07267 9.59857 4.80006 9.6ZM4.80006 1.2C4.08804 1.2 3.39201 1.41114 2.79998 1.80671C2.20796 2.20228 1.74653 2.76453 1.47405 3.42234C1.20157 4.08015 1.13028 4.80399 1.26919 5.50232C1.4081 6.20066 1.75097 6.84211 2.25444 7.34558C2.75792 7.84905 3.39939 8.19192 4.09773 8.33082C4.79606 8.46973 5.51991 8.39844 6.17773 8.12596C6.83556 7.85349 7.39781 7.39207 7.79338 6.80005C8.18896 6.20803 8.4001 5.51201 8.4001 4.8C8.39915 3.84551 8.01955 2.93039 7.34462 2.25547C6.66969 1.58055 5.75456 1.20095 4.80006 1.2Z"
                    fill="#6B7280"/>
                <path
                    d="M11.4001 12C11.241 12 11.0884 11.9367 10.9759 11.8242L8.57589 9.4242C8.46659 9.31104 8.40611 9.15948 8.40748 9.00216C8.40885 8.84484 8.47195 8.69436 8.5832 8.58311C8.69444 8.47187 8.84493 8.40876 9.00225 8.4074C9.15957 8.40603 9.31114 8.46651 9.4243 8.5758L11.8243 10.9758C11.9082 11.0597 11.9653 11.1666 11.9885 11.283C12.0116 11.3994 11.9997 11.52 11.9543 11.6296C11.9089 11.7392 11.832 11.8329 11.7334 11.8988C11.6348 11.9648 11.5188 12 11.4001 12Z"
                    fill="#6B7280"/>
              </g>
              <defs>
                <clipPath id="clip0_824_1500">
                  <rect width="12" height="12" fill="white"/>
                </clipPath>
              </defs>
            </svg>
          </div>
          <input type="text" :id="itemKey('input')"
                 @input="searchForItems($event.currentTarget.value)"
                 class="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block w-full pr-10 p-2.5  dark:bg-gray-700 dark:border-gray-600 dark:placeholder-gray-400 dark:text-white dark:focus:ring-blue-500 dark:focus:border-blue-500 lg:min-w-[180px]"
                 placeholder="Search" autocomplete="off">
        </div>

        <div class="max-h-80 overflow-y-auto pr-1">
          <div class="grid grid-cols-2 lg:grid-cols-3 gap-3">
            <div class="flex items-center" v-for="item in visibleItems(items)" :key="item.value">
              <input
                     :checked="item.isRefined"
                     @change="refine(item.value); track('search_filter', { filter: attribute, value: item.value })"
                     :id="itemKey('inputs', item.value)" type="checkbox"
                     class="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 dark:focus:ring-blue-600 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600">
              <label for="default-checkbox" class="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300">
                {{ displayLabel(item.value) }} ({{ item.count.toLocaleString() }})
              </label>
            </div>
          </div>
        </div>
        <button v-if="items.length > collapsedLimit" type="button"
                @click="expanded = !expanded"
                class="mt-3 text-sm font-medium text-blue-600 hover:underline dark:text-blue-500">
          {{ expanded ? $t('labels.show_less') : $t('labels.show_all', { count: items.length }) }}
        </button>
      </template>
    </ais-refinement-list>
    <AisStore v-if="store" :store="store" :attribute="attribute" />
</template>
<script setup lang="ts">
import { AisInstantSearch, AisNumericMenu, AisSearchBox, AisHits, AisMenuSelect, AisConfigure, AisPagination, AisStats, AisRefinementList, AisHighlight } from 'vue-instantsearch/vue3/es'
import { initDropdowns } from 'flowbite'
import { useSearchStore } from '~/stores/searchStore'

const searchStore = useSearchStore();
const { track } = useAnalytics();

const props = defineProps({
  label: {
    type: String,
    required: true
  },
  store: {
    type: String,
    required: false
  },
  searchable: {
    type: Boolean,
    default: true
  },
  attribute: {
    type: String,
    required: true
  }
});

function itemKey(prefix, id = null) {
  return props.attribute.replaceAll('.', '-') + '-' + prefix + '-item' + (id ? '-' + id : '');
}

// Collapse long lists (e.g. buildings) to the top results, with a "Show all"
// toggle; the list scrolls within a capped height once expanded.
const expanded = ref(false);
const collapsedLimit = 18;
function visibleItems(items) {
  return expanded.value ? items : items.slice(0, collapsedLimit);
}

// Refinement still uses the slug (item.value); only the displayed text is mapped
// to the human-readable name/label from areas.json. Falls back to the slug.
function displayLabel(value) {
  if (props.attribute === 'location.city.slug') {
    return searchStore.areaCities[value] ?? value;
  }
  if (props.attribute === 'location.district.slug') {
    return searchStore.areaDistricts[value] ?? value;
  }
  if (props.attribute === 'location.geocode') {
    return searchStore.geocodeLabels[value] ?? value;
  }
  return value;
}

onMounted(() => {
    initDropdowns();
})
</script>